import { FC, useMemo, useState } from 'react'
import { EChartOption } from 'echarts'
import CardFilterButton from 'src/components/CardFilterButton'
import { QuestionMarkTooltip } from 'src/components/QuestionMarkTooltip'
import { Skeleton } from 'src/components/Skeleton'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { formatOptionalCount, numberWithCommas } from 'src/utils/numberFormat'
import { ContentPerformanceFilterModal } from './ContentPerformanceFilterModal'
import styles from './contentPerformanceSection.module.scss'
import {
  ContentPerformanceMockData,
  ContentPerformancePlatformData,
} from '../../pages/ContentAnalysisDetail/mockData/contentPerformanceMockData'
import { EChart } from '../EChart'

const PLATFORM_COLORS: Record<string, string> = {
  facebook: '#5A6FC0',
  instagram: '#E3483F',
  linkedin: '#004182',
  tiktok: '#00F2EA',
  twitter: '#FF0000',
  youtube: '#FF0000',
}

type SortField = keyof Omit<ContentPerformancePlatformData, 'postsBreakdown'>
type SortDirection = 'asc' | 'desc'

interface RangeFilter {
  less: boolean
  more: boolean
  number: string
}

// Real dimensions from contentPerformanceSection.module.scss: .chartPercentage
// is 24px/600 and .chartLabel 14px, both inside the FIXED 180x180 .chartWrapper
// — that box cannot shift, so only its readout is skeletoned (it otherwise
// reports a confident "0%" / "No data" before any data has arrived). The
// donut itself is left alone: its container is fixed height.
// .tableCell is 16px padding around 14px text -> a 48px row.
const CHART_PERCENTAGE_HEIGHT = 28
const CHART_PERCENTAGE_WIDTH = 64
const CHART_LABEL_HEIGHT = 16
const CHART_LABEL_WIDTH = 78
const CELL_HEIGHT = 16
const PLATFORM_CELL_WIDTH = 72
const VALUE_CELL_WIDTH = 48
// Row count is only knowable from the platform list the page already has; it
// falls back to the common 3-platform case so the table still reserves height.
const FALLBACK_SKELETON_ROWS = 3

// ── Boundary normalization ──────────────────────────────────────────────────
// The API response is cast to ContentPerformanceMockData without validation,
// so "number" fields can arrive as null/undefined: views/erv are null BY
// DESIGN when a platform hides views (sm.back service/contentViews.js), and
// followers is a Mongo `$max` that is null when no document carries it (new
// pages / not scraped yet). Everything below this line works on normalized
// rows: plain counts default to 0, the three genuinely-unknowable metrics
// stay `null` and render as "—".
const toCount = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : 0
const toOptionalCount = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

export const normalizeContentPerformancePlatform = (
  raw: Partial<ContentPerformancePlatformData> | null | undefined
): ContentPerformancePlatformData => ({
  comments: toCount(raw?.comments),
  err: toCount(raw?.err),
  erv: toOptionalCount(raw?.erv),
  followers: toOptionalCount(raw?.followers),
  platform: typeof raw?.platform === 'string' ? raw.platform : 'unknown',
  posts: toCount(raw?.posts),
  postsBreakdown: {
    textOnly: toCount(raw?.postsBreakdown?.textOnly),
    withPicture: toCount(raw?.postsBreakdown?.withPicture),
    withVideo: toCount(raw?.postsBreakdown?.withVideo),
  },
  reactions: toCount(raw?.reactions),
  shares: toCount(raw?.shares),
  views: toOptionalCount(raw?.views),
})

export const normalizeContentPerformancePlatforms = (
  data: Partial<ContentPerformanceMockData> | null | undefined
): ContentPerformancePlatformData[] =>
  (Array.isArray(data?.platforms) ? data.platforms : [])
    .filter(Boolean)
    .map(normalizeContentPerformancePlatform)

interface ContentPerformanceSectionProps {
  data: ContentPerformanceMockData
  isLoading?: boolean
  platforms?: string[]
}

export const ContentPerformanceSection: FC<ContentPerformanceSectionProps> = ({
  data,
  isLoading = false,
  platforms,
}) => {
  const [hoveredPlatform, setHoveredPlatform] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('platform')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  // Seeded from the `platforms` prop when available: this component now also
  // mounts during its first load, when `data.platforms` is still empty, and a
  // state initializer only ever runs on that first mount.
  const platformRows = useMemo(
    () => normalizeContentPerformancePlatforms(data),
    [data]
  )
  // Normalized rows for the filter modal too, so it never sees a missing list
  // or null counts.
  const modalData = useMemo(() => ({ platforms: platformRows }), [platformRows])
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(
    new Set(['all', ...(platforms ?? platformRows.map((p) => p.platform))])
  )
  const [rangeFilters, setRangeFilters] = useState<Record<string, RangeFilter>>(
    {
      comments: { less: false, more: false, number: '' },
      err: { less: false, more: false, number: '' },
      erv: { less: false, more: false, number: '' },
      followers: { less: false, more: false, number: '' },
      posts: { less: false, more: false, number: '' },
      reactions: { less: false, more: false, number: '' },
      shares: { less: false, more: false, number: '' },
      views: { less: false, more: false, number: '' },
    }
  )

  // Apply filters to data
  const filteredPlatforms = useMemo(() => {
    return platformRows.filter((platform) => {
      // Platform filter
      if (
        !selectedPlatforms.has('all') &&
        !selectedPlatforms.has(platform.platform)
      ) {
        return false
      }

      // Range filters
      for (const [key, filter] of Object.entries(rangeFilters)) {
        if (filter.number && (filter.more || filter.less)) {
          const value = platform[key as keyof typeof platform] as number | null
          // An unknown count can satisfy no range: exclude rather than treat as 0.
          if (value === null || value === undefined) return false
          const threshold = parseFloat(filter.number)
          if (isNaN(threshold)) continue

          if (filter.more && value <= threshold) return false
          if (filter.less && value >= threshold) return false
        }
      }

      return true
    })
  }, [platformRows, selectedPlatforms, rangeFilters])

  const totalPosts = filteredPlatforms.reduce((sum, p) => sum + p.posts, 0)

  const chartData = useMemo(() => {
    return filteredPlatforms.map((platform) => ({
      itemStyle: { color: PLATFORM_COLORS[platform.platform] || '#8080FF' },
      name: platform.platform,
      value: platform.posts,
    }))
  }, [filteredPlatforms])

  const displayPlatform =
    hoveredPlatform || filteredPlatforms[0]?.platform || ''
  const displayPercentage = useMemo(() => {
    if (!displayPlatform || filteredPlatforms.length === 0) return '0'
    const platform = filteredPlatforms.find(
      (p) => p.platform === displayPlatform
    )
    if (!platform) return '0'
    return Math.round((platform.posts / totalPosts) * 100)
  }, [filteredPlatforms, displayPlatform, totalPosts])

  const chartOptions: EChartOption<EChartOption.SeriesPie> = useMemo(() => {
    return {
      series: [
        {
          data: chartData,
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 3,
          },
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
          radius: ['72%', '92%'],
          type: 'pie',
        },
      ],
      tooltip: {
        show: false,
      },
    }
  }, [chartData])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedPlatforms = useMemo(() => {
    return [...filteredPlatforms].sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }

      // Unknown (null) metrics sort last in either direction.
      if (typeof aVal === 'number') return -1
      if (typeof bVal === 'number') return 1
      return 0
    })
  }, [filteredPlatforms, sortField, sortDirection])

  const totals = useMemo(() => {
    return filteredPlatforms.reduce(
      (acc, p) => ({
        comments: acc.comments + p.comments,
        // Like views: unknown followers are skipped; all unknown -> null.
        followers:
          typeof p.followers === 'number'
            ? (acc.followers ?? 0) + p.followers
            : acc.followers,
        posts: acc.posts + p.posts,
        reactions: acc.reactions + p.reactions,
        shares: acc.shares + p.shares,
        // Unknown views are skipped, not added as 0; all unknown -> null.
        views:
          typeof p.views === 'number' ? (acc.views ?? 0) + p.views : acc.views,
      }),
      {
        comments: 0,
        followers: null as number | null,
        posts: 0,
        reactions: 0,
        shares: 0,
        views: null as number | null,
      }
    )
  }, [filteredPlatforms])

  const avgErr = Math.round(
    filteredPlatforms.length > 0
      ? filteredPlatforms.reduce((sum, p) => sum + p.err, 0) /
          filteredPlatforms.length
      : 0
  )
  // Averaged over the platforms that HAVE an ERV; null when none does.
  const ervKnown = filteredPlatforms.filter(
    (p): p is typeof p & { erv: number } => typeof p.erv === 'number'
  )
  const avgErv =
    ervKnown.length > 0
      ? Math.round(
          ervKnown.reduce((sum, p) => sum + p.erv, 0) / ervKnown.length
        )
      : null

  const columns: {
    field: SortField
    hasTooltip?: boolean
    label: string
    tooltipText?: string
  }[] = [
    { field: 'platform', label: 'Platform' },
    { field: 'posts', hasTooltip: true, label: 'Posts' },
    { field: 'reactions', label: 'Reactions' },
    { field: 'comments', label: 'Comments' },
    { field: 'shares', label: 'Shares' },
    { field: 'views', label: 'Views' },
    {
      field: 'followers',
      hasTooltip: true,
      label: 'Followers',
      tooltipText:
        "The follower count reflects the profile's followers when posts from a single profile/page are selected, and the combined followers when posts from multiple profiles/pages are selected.",
    },
    { field: 'err', label: 'ERR' },
    { field: 'erv', label: 'ERV' },
  ]

  const skeletonRowCount = platforms?.length || FALLBACK_SKELETON_ROWS

  const capitalizeFirstLetter = (str: string | undefined) => {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  return (
    <div className={styles.contentPerformanceSection}>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <span className={styles.title}>Content Performance</span>
          <QuestionMarkTooltip
            overlay="Content performance metrics across platforms"
            placement="top"
          />
        </div>
        <CardFilterButton onClick={() => setIsFilterModalOpen(true)} />
      </div>

      <div className={styles.content}>
        <div className={styles.chartWrapper}>
          <EChart
            height={180}
            onMouseOut={() => setHoveredPlatform(null)}
            onMouseOver={(event) => {
              if (event.name) {
                setHoveredPlatform(event.name as string)
              }
            }}
            options={chartOptions}
            width={180}
          />
          <div className={styles.chartCenter}>
            {isLoading ? (
              <>
                <Skeleton
                  height={CHART_PERCENTAGE_HEIGHT}
                  width={CHART_PERCENTAGE_WIDTH}
                />
                <Skeleton
                  height={CHART_LABEL_HEIGHT}
                  width={CHART_LABEL_WIDTH}
                />
              </>
            ) : (
              <>
                <span className={styles.chartPercentage}>
                  {displayPercentage}%
                </span>
                <span className={styles.chartLabel}>
                  {displayPlatform && (
                    <>
                      <span className={styles.chartLabelIcon}>
                        {SOCIAL_ICONS[displayPlatform]}
                      </span>
                      {platformDisplayLabel(
                        displayPlatform,
                        capitalizeFirstLetter(displayPlatform)
                      )}
                    </>
                  )}
                  {!displayPlatform && 'No data'}
                </span>
              </>
            )}
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.tableHeader}>
                {columns.map((col) => (
                  <th
                    className={styles.tableHeaderCell}
                    key={col.field}
                    onClick={() => handleSort(col.field)}
                  >
                    <div className={styles.headerContent}>
                      {col.field === 'followers' ? (
                        <span className={styles.followersHeader}>
                          {col.label}
                          <div className={styles.followersTooltip}>
                            {col.tooltipText}
                          </div>
                        </span>
                      ) : (
                        col.label
                      )}
                    </div>
                    <span className={styles.sortIcon}>
                      {sortField === col.field
                        ? sortDirection === 'asc'
                          ? '↑'
                          : '↓'
                        : '↕'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr className={styles.tableRow} key={rowIndex}>
                    {columns.map((col) => (
                      <td className={styles.tableCell} key={col.field}>
                        <Skeleton
                          height={CELL_HEIGHT}
                          width={
                            col.field === 'platform'
                              ? PLATFORM_CELL_WIDTH
                              : VALUE_CELL_WIDTH
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              {!isLoading &&
                sortedPlatforms.map((platform) => (
                  <tr className={styles.tableRow} key={platform.platform}>
                    <td className={styles.tableCell}>
                      {platformDisplayLabel(
                        platform.platform,
                        capitalizeFirstLetter(platform.platform)
                      )}
                    </td>
                    <td className={styles.tableCell}>
                      <div className={styles.postsCell}>
                        {platform.posts}
                        <QuestionMarkTooltip
                          iconType="info"
                          overlay={
                            <div className={styles.tooltipContent}>
                              <div className={styles.tooltipColumn}>
                                <span className={styles.tooltipLabel}>
                                  With Picture
                                </span>
                                <span className={styles.tooltipValue}>
                                  {platform.postsBreakdown.withPicture}
                                </span>
                              </div>
                              <div className={styles.tooltipColumn}>
                                <span className={styles.tooltipLabel}>
                                  With Video
                                </span>
                                <span className={styles.tooltipValue}>
                                  {platform.postsBreakdown.withVideo}
                                </span>
                              </div>
                              <div className={styles.tooltipColumn}>
                                <span className={styles.tooltipLabel}>
                                  Text-only
                                </span>
                                <span className={styles.tooltipValue}>
                                  {platform.postsBreakdown.textOnly}
                                </span>
                              </div>
                            </div>
                          }
                          overlayClassName="tooltipWrapper"
                          overlayInnerClassName={styles.tooltipOverlay}
                          placement="top"
                        />
                      </div>
                    </td>
                    <td className={styles.tableCell}>
                      {numberWithCommas(platform.reactions)}
                    </td>
                    <td className={styles.tableCell}>{platform.comments}</td>
                    <td className={styles.tableCell}>{platform.shares}</td>
                    <td className={styles.tableCell}>
                      {formatOptionalCount(platform.views)}
                    </td>
                    <td className={styles.tableCell}>
                      {formatOptionalCount(platform.followers)}
                    </td>
                    <td className={styles.tableCell}>{platform.err}%</td>
                    <td className={styles.tableCell}>
                      {platform.erv === null ? '—' : `${platform.erv}%`}
                    </td>
                  </tr>
                ))}
              {isLoading && (
                <tr className={`${styles.tableRow} ${styles.totalRow}`}>
                  <td className={styles.tableCell}>Total</td>
                  {columns.slice(1).map((col) => (
                    <td className={styles.tableCell} key={col.field}>
                      <Skeleton height={CELL_HEIGHT} width={VALUE_CELL_WIDTH} />
                    </td>
                  ))}
                </tr>
              )}
              {!isLoading && (
                <tr className={`${styles.tableRow} ${styles.totalRow}`}>
                  <td className={styles.tableCell}>Total</td>
                  <td className={styles.tableCell}>
                    <div className={styles.postsCell}>
                      {totals.posts}
                      <QuestionMarkTooltip
                        iconType="info"
                        overlay={
                          <div className={styles.tooltipContent}>
                            <div className={styles.tooltipColumn}>
                              <span className={styles.tooltipLabel}>
                                With Picture
                              </span>
                              <span className={styles.tooltipValue}>
                                {filteredPlatforms.reduce(
                                  (sum, p) =>
                                    sum + p.postsBreakdown.withPicture,
                                  0
                                )}
                              </span>
                            </div>
                            <div className={styles.tooltipColumn}>
                              <span className={styles.tooltipLabel}>
                                With Video
                              </span>
                              <span className={styles.tooltipValue}>
                                {filteredPlatforms.reduce(
                                  (sum, p) => sum + p.postsBreakdown.withVideo,
                                  0
                                )}
                              </span>
                            </div>
                            <div className={styles.tooltipColumn}>
                              <span className={styles.tooltipLabel}>
                                Text-only
                              </span>
                              <span className={styles.tooltipValue}>
                                {filteredPlatforms.reduce(
                                  (sum, p) => sum + p.postsBreakdown.textOnly,
                                  0
                                )}
                              </span>
                            </div>
                          </div>
                        }
                        overlayClassName="tooltipWrapper"
                        overlayInnerClassName={styles.tooltipOverlay}
                        placement="top"
                      />
                    </div>
                  </td>
                  <td className={styles.tableCell}>
                    {numberWithCommas(totals.reactions)}
                  </td>
                  <td className={styles.tableCell}>{totals.comments}</td>
                  <td className={styles.tableCell}>{totals.shares}</td>
                  <td className={styles.tableCell}>
                    {formatOptionalCount(totals.views)}
                  </td>
                  <td className={styles.tableCell}>
                    {formatOptionalCount(totals.followers)}
                  </td>
                  <td className={styles.tableCell}>{avgErr}%</td>
                  <td className={styles.tableCell}>
                    {avgErv === null ? '—' : `${avgErv}%`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ContentPerformanceFilterModal
        data={modalData}
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onSave={(platforms, filters) => {
          setSelectedPlatforms(platforms)
          setRangeFilters(filters)
          setIsFilterModalOpen(false)
        }}
        rangeFilters={rangeFilters}
        selectedPlatforms={selectedPlatforms}
      />
    </div>
  )
}
